import { canAccessContextPath, defaultContextPath } from "../../components/AppLayout/navigation";
import SwitchFirmLayout from "../../components/AppLayout/SwitchFirmLayout";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Navigate, useLocation } from "react-router-dom";
import {
  getAuthenticatedUser,
  loadAuthenticatedUser,
} from "../../helpers/auth-session";

const Authmiddleware = ({ children, layout: Layout, isAuthProtected }) => {
  const location = useLocation();
  const [checkingSession, setCheckingSession] = useState(
    isAuthProtected && !getAuthenticatedUser()
  );
  const [authenticated, setAuthenticated] = useState(
    Boolean(getAuthenticatedUser())
  );
  const [sessionError, setSessionError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!isAuthProtected) return;
    if (getAuthenticatedUser()) {
      setAuthenticated(true);
      setCheckingSession(false);
      setSessionError(false);
      return;
    }
    let active = true;
    let retryTimer;
    setCheckingSession(true);
    setSessionError(false);
    const check = async () => {
      const delays = [0, 750, 1500, 3000];
      for (let attempt = 0; attempt < delays.length && active; attempt += 1) {
        if (delays[attempt])
          await new Promise((resolve) => { retryTimer = setTimeout(resolve, delays[attempt]); });
        if (!active) return;
        try {
          await loadAuthenticatedUser();
          if (active) { setAuthenticated(true); setCheckingSession(false); }
          return;
        } catch (error) {
          if (!active) return;
          if (error?.status === 401 || error?.status === 403) {
            setAuthenticated(false);
            setCheckingSession(false);
            return;
          }
          const transient = !error?.status || error.status === 408 || error.status === 429 || error.status >= 500;
          if (!transient || attempt === delays.length - 1) {
            setSessionError(true);
            setCheckingSession(false);
            return;
          }
        }
      }
    };
    check();
    return () => { active = false; clearTimeout(retryTimer); };
  }, [isAuthProtected, retry]);

  if (isAuthProtected && checkingSession) return <p role="status">Checking your session…</p>;

  if (isAuthProtected && sessionError) return <main role="alert">
    <p>Unable to check your session right now. Please retry.</p>
    <button type="button" onClick={() => { setCheckingSession(true); setRetry((value) => value + 1); }}>Retry</button>
  </main>;

  if (isAuthProtected && !authenticated) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  if (isAuthProtected && getAuthenticatedUser()?.requiresFirmSelection && location.pathname !== "/switch-firm")
    return <Navigate to="/switch-firm" replace />;
  if (isAuthProtected && !canAccessContextPath(location.pathname, getAuthenticatedUser()))
    return <Navigate to={defaultContextPath(getAuthenticatedUser())} replace />;
  if (isAuthProtected && location.pathname === "/switch-firm")
    return <SwitchFirmLayout>{children}</SwitchFirmLayout>;
  return <Layout>{children}</Layout>;
};

export default Authmiddleware;
